// Observability Dashboard.
//
// First-thing-you-see when entering the Merchant RPA Agent. Loads from
// /finance/rpa/observability/summary in one round trip.
//
// Layout:
//   Row 1: 4 hero KPI cards (24h runs, success rate, repair queue, 30d cost)
//   Row 2: 30-day run trend (stacked area: succeeded / failed / repair / cancelled)
//   Row 3: Two-up — cost breakdown by purpose (donut) + top failing workflows (table)
//   Row 4: Recent failures list + secondary KPIs strip (active merchants, workflows, docs, matches)
//
// All charts come from AgentUIKit so the look matches the rest of the
// finance agent surfaces.
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Stack, Paper, Typography, Chip, Alert, IconButton,
    Table, TableHead, TableBody, TableRow, TableCell,
    LinearProgress, Tooltip, Divider, alpha,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import PaymentsIcon from '@mui/icons-material/Payments';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import DescriptionIcon from '@mui/icons-material/Description';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { TrendChart, DonutChart, CHART_COLORS } from './AgentUIKit';
import { rpaObservabilitySummary } from './api';
import {
    KpiCard, KpiGrid, SectionPaper, EmptyState, Toolbar, StatusChip,
    RPA_COLORS, formatDateLong,
} from './rpaTheme';


export default function ObservabilityDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await rpaObservabilitySummary();
            if (r?.data?.success) setData(r.data.data);
            else setError(r?.data?.message || 'Failed to load summary');
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) return <LinearProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return null;

    const k = data.kpis || {};
    const successRate = k.success_rate_24h;
    const successColor = successRate == null ? RPA_COLORS.neutral
        : successRate >= 95 ? RPA_COLORS.success
        : successRate >= 80 ? RPA_COLORS.warning
        : RPA_COLORS.danger;

    return (
        <Box>
            <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" onClick={load} disabled={loading}>
                    <RefreshIcon fontSize="small" />
                </IconButton>
            </Stack>

            {/* Hero KPIs */}
            <KpiGrid>
                <KpiCard
                    label="Runs (24h)"
                    value={k.runs_24h ?? 0}
                    icon={<PlayArrowIcon fontSize="small" />}
                    accent={RPA_COLORS.robot}
                    hint={k.runs_24h_breakdown
                        ? `${k.runs_24h_breakdown.queued || 0} queued · ${k.runs_24h_breakdown.running || 0} running`
                        : undefined}
                />
                <KpiCard
                    label="Success rate (24h)"
                    value={successRate != null ? `${successRate}%` : '—'}
                    icon={<CheckCircleIcon fontSize="small" />}
                    accent={successColor}
                    hint={k.avg_run_duration_ms_7d
                        ? `Avg ${formatDuration(k.avg_run_duration_ms_7d)} (7d)`
                        : undefined}
                />
                <KpiCard
                    label="Failed runs"
                    value={k.needs_repair_now ?? 0}
                    icon={<BuildIcon fontSize="small" />}
                    accent={(k.needs_repair_now || 0) > 0
                        ? RPA_COLORS.danger : RPA_COLORS.neutral}
                    hint={(k.needs_repair_now || 0) > 0
                        ? 'Open Rerun tab to retry' : 'All clear'}
                />
                <KpiCard
                    label="Gemini cost (30d)"
                    value={formatMoney(k.cost_usd_30d)}
                    icon={<PaymentsIcon fontSize="small" />}
                    accent={RPA_COLORS.info}
                    hint="All RPA-attributed calls"
                />
            </KpiGrid>

            {/* Run trend */}
            <SectionPaper sx={{ mb: 2.5, p: 0 }}>
                <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${RPA_COLORS.border}` }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <TrendingUpIcon fontSize="small" sx={{ color: RPA_COLORS.robot }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Run volume — last 30 days
                        </Typography>
                    </Stack>
                </Box>
                <Box sx={{ p: 2, height: 220 }}>
                    <RunTrendChart data={data.run_trend_30d || []} />
                </Box>
            </SectionPaper>

            {/* Cost breakdown + Top failures */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2.5,
                mb: 2.5,
            }}>
                <CostBreakdownPanel
                    rows={data.cost_breakdown_30d || []}
                    total={k.cost_usd_30d || 0}
                />
                <TopFailingPanel rows={data.top_failing_workflows_30d || []} />
            </Box>

            {/* Recent failures + secondary KPIs */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2.5,
            }}>
                <RecentFailuresPanel rows={data.recent_failures || []} />
                <SecondaryKpisPanel kpis={k} />
            </Box>
        </Box>
    );
}


function RunTrendChart({ data }) {
    if (!data || data.length === 0) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <Typography variant="body2" sx={{ color: RPA_COLORS.textMuted }}>
                    No runs in the last 30 days yet.
                </Typography>
            </Stack>
        );
    }
    return (
        <TrendChart
            data={data}
            xKey="day"
            series={[
                { key: 'succeeded',    name: 'Succeeded',    color: RPA_COLORS.success },
                { key: 'failed',       name: 'Failed',       color: RPA_COLORS.danger },
                { key: 'rerun',        name: 'Rerun',        color: RPA_COLORS.info },
                { key: 'cancelled',    name: 'Cancelled',    color: RPA_COLORS.neutral },
            ]}
            stacked
            type="area"
            height={180}
        />
    );
}


function CostBreakdownPanel({ rows, total }) {
    const colors = [
        RPA_COLORS.robot, RPA_COLORS.accent, RPA_COLORS.info,
        RPA_COLORS.warning, RPA_COLORS.neutral,
    ];
    const data = (rows || []).map((r, i) => ({
        name: humanisePurpose(r.purpose),
        value: r.cost_usd,
        purpose: r.purpose,
        calls: r.calls,
        color: colors[i % colors.length],
    }));
    const hasData = data.some((d) => d.value > 0);

    return (
        <SectionPaper sx={{ p: 0 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${RPA_COLORS.border}` }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <PaymentsIcon fontSize="small" sx={{ color: RPA_COLORS.info }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Gemini cost — by purpose (30d)
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Chip label={formatMoney(total)} size="small"
                          sx={{ fontWeight: 700, color: RPA_COLORS.info,
                                bgcolor: alpha(RPA_COLORS.info, 0.10) }} />
                </Stack>
            </Box>
            <Box sx={{ p: 2 }}>
                {!hasData ? (
                    <Stack alignItems="center" sx={{ py: 4, color: RPA_COLORS.textMuted }}>
                        <Typography variant="body2">
                            No Gemini-attributed cost in the last 30 days.
                        </Typography>
                    </Stack>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 160, height: 160, flexShrink: 0 }}>
                            <DonutChart data={data} dataKey="value" nameKey="name"
                                        height={160} colors={data.map((d) => d.color)} />
                        </Box>
                        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                            {data.map((d) => (
                                <Stack key={d.purpose} direction="row" spacing={1}
                                       alignItems="center">
                                    <Box sx={{
                                        width: 10, height: 10, borderRadius: '50%',
                                        background: d.color, flexShrink: 0,
                                    }} />
                                    <Typography variant="caption" sx={{
                                        flex: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontWeight: 600,
                                    }}>
                                        {d.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontFamily: 'monospace',
                                        color: RPA_COLORS.textMuted,
                                    }}>
                                        {d.calls} call{d.calls === 1 ? '' : 's'}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 700,
                                        minWidth: 60,
                                        textAlign: 'right',
                                    }}>
                                        {formatMoney(d.value)}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Box>
        </SectionPaper>
    );
}


function TopFailingPanel({ rows }) {
    return (
        <SectionPaper sx={{ p: 0 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${RPA_COLORS.border}` }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ErrorOutlineIcon fontSize="small" sx={{ color: RPA_COLORS.danger }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Top failing workflows (30d)
                    </Typography>
                </Stack>
            </Box>
            <Box>
                {rows.length === 0 ? (
                    <Stack alignItems="center" sx={{ py: 4, color: RPA_COLORS.textMuted }}>
                        <CheckCircleIcon sx={{ fontSize: 28, mb: 1, color: RPA_COLORS.success }} />
                        <Typography variant="body2">
                            No failing workflows in the last 30 days.
                        </Typography>
                    </Stack>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Workflow</TableCell>
                                <TableCell align="right">Failures</TableCell>
                                <TableCell align="right">Rate</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.workflow_id}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {r.workflow_name} <span style={{
                                                color: RPA_COLORS.textFaint,
                                                fontWeight: 400,
                                            }}>v{r.workflow_version}</span>
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                        {r.failures}/{r.total_runs}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            size="small"
                                            label={`${r.failure_rate_pct}%`}
                                            sx={{
                                                fontWeight: 700,
                                                color: r.failure_rate_pct >= 50 ? RPA_COLORS.danger
                                                    : r.failure_rate_pct >= 20 ? RPA_COLORS.warning
                                                    : RPA_COLORS.neutral,
                                                bgcolor: alpha(
                                                    r.failure_rate_pct >= 50 ? RPA_COLORS.danger
                                                    : r.failure_rate_pct >= 20 ? RPA_COLORS.warning
                                                    : RPA_COLORS.neutral, 0.12),
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </SectionPaper>
    );
}


function RecentFailuresPanel({ rows }) {
    return (
        <SectionPaper sx={{ p: 0 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${RPA_COLORS.border}` }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <BuildIcon fontSize="small" sx={{ color: RPA_COLORS.warning }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Recent failures
                    </Typography>
                </Stack>
            </Box>
            <Box>
                {rows.length === 0 ? (
                    <Stack alignItems="center" sx={{ py: 4, color: RPA_COLORS.textMuted }}>
                        <CheckCircleIcon sx={{ fontSize: 28, mb: 1, color: RPA_COLORS.success }} />
                        <Typography variant="body2">No recent failures.</Typography>
                    </Stack>
                ) : (
                    <Stack divider={<Divider />}>
                        {rows.map((r) => (
                            <Box key={r.run_id} sx={{ p: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1}
                                       sx={{ mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Run #{r.run_id}
                                    </Typography>
                                    <StatusChip status={r.status} />
                                    <Box sx={{ flex: 1 }} />
                                    <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                        {formatDateLong(r.finished_at || r.started_at)}
                                    </Typography>
                                </Stack>
                                {r.error_message && (
                                    <Typography variant="caption" sx={{
                                        color: RPA_COLORS.textMuted,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}>
                                        {r.error_message}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </SectionPaper>
    );
}


function SecondaryKpisPanel({ kpis }) {
    const items = [
        {
            label: 'Active merchants',
            value: kpis.active_merchants,
            icon: <StorefrontIcon fontSize="small" />,
            color: RPA_COLORS.accent,
        },
        {
            label: 'Active workflows',
            value: kpis.active_workflows,
            icon: <AutoAwesomeMotionIcon fontSize="small" />,
            color: RPA_COLORS.robot,
        },
        {
            label: 'Docs extracted (7d)',
            value: kpis.docs_extracted_7d,
            icon: <DescriptionIcon fontSize="small" />,
            color: RPA_COLORS.info,
            sub: kpis.docs_quarantined_7d
                ? `${kpis.docs_quarantined_7d} need review` : null,
        },
        {
            label: 'Active matches',
            value: kpis.matches_active_now,
            icon: <CompareArrowsIcon fontSize="small" />,
            color: RPA_COLORS.success,
            sub: (kpis.matches_breakdown_now?.unmatched || 0) > 0
                ? `${kpis.matches_breakdown_now.unmatched} unmatched` : null,
        },
    ];

    return (
        <SectionPaper sx={{ p: 0 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${RPA_COLORS.border}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Module health
                </Typography>
            </Box>
            <Box sx={{ p: 1.5 }}>
                <Stack divider={<Divider sx={{ my: 0.5 }} />} spacing={0.5}>
                    {items.map((item) => (
                        <Stack key={item.label} direction="row" alignItems="center"
                               spacing={1.5} sx={{ p: 1 }}>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: 1,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: alpha(item.color, 0.12),
                                color: item.color,
                                flexShrink: 0,
                            }}>
                                {item.icon}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{
                                    color: RPA_COLORS.textMuted,
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    letterSpacing: 0.4,
                                    fontSize: 10,
                                }}>
                                    {item.label}
                                </Typography>
                                {item.sub && (
                                    <Typography variant="caption" sx={{
                                        display: 'block',
                                        color: RPA_COLORS.warning,
                                        fontWeight: 500,
                                    }}>
                                        {item.sub}
                                    </Typography>
                                )}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {item.value ?? 0}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        </SectionPaper>
    );
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function humanisePurpose(p) {
    if (!p) return 'Unknown';
    const map = {
        rpa_doc_extract:        'Document extraction',
        rpa_selector_heal:      'Selector self-heal',
        rpa_recon_adjudicate:   'Match adjudication',
        rpa_vision:             'Vision',
    };
    return map[p] || p.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function formatMoney(v) {
    if (v == null) return '—';
    const num = Number(v);
    if (num === 0) return '$0.00';
    if (num < 0.01) return `$${num.toFixed(6)}`;
    if (num < 1) return `$${num.toFixed(4)}`;
    return `$${num.toFixed(2)}`;
}

function formatDuration(ms) {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}
